import React, { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
  return useContext(ThemeContext)
}

export const ThemeProvider = ({ children }) => {
  // Theme source states: 'system', 'user'
  const [themeSource, setThemeSource] = useState(() => {
    const savedThemeSource = localStorage.getItem('themeSource')
    return savedThemeSource || 'system'
  })

  // System preference for dark mode
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  // Actual dark mode state based on source and preferences
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedThemeSource = localStorage.getItem('themeSource')
    const savedTheme = localStorage.getItem('themeMode')

    if (savedThemeSource === 'user' && savedTheme) {
      return savedTheme === 'dark'
    }

    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  // Save theme preferences to localStorage
  useEffect(() => {
    localStorage.setItem('themeMode', isDarkMode ? 'dark' : 'light')
    localStorage.setItem('themeSource', themeSource)
  }, [isDarkMode, themeSource])

  // Listen for changes in system color scheme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e) => {
      setSystemPrefersDark(e.matches)

      // Only update theme if source is set to system
      if (themeSource === 'system') {
        setIsDarkMode(e.matches)
      }
    }

    // Add event listener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange)
    }

    // Clean up
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [themeSource])

  // Toggle theme and set source to user
  const toggleTheme = () => {
    setThemeSource('user')
    setIsDarkMode((prev) => !prev)
  }

  // Reset to system preference
  const resetToSystemTheme = () => {
    setThemeSource('system')
    setIsDarkMode(systemPrefersDark)
  }

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        themeSource,
        resetToSystemTheme,
        systemPrefersDark,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export default ThemeContext
